import { useState, useEffect, useCallback, useMemo } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { MenuItem, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

// Canonical category order & Bangla/English labels (kept in sync with menu_categories seed data)
const CATEGORY_OPTIONS = [
  { value: 'about', label: 'about — প্রতিষ্ঠান পরিচিতি' },
  { value: 'admin', label: 'admin — প্রশাসনিক তথ্য' },
  { value: 'faculty', label: 'faculty — শিক্ষক ও কর্মচারী' },
  { value: 'students', label: 'students — শিক্ষার্থী' },
  { value: 'academic', label: 'academic — একাডেমিক তথ্য' },
  { value: 'cocurricular', label: 'cocurricular — সহপাঠ্যক্রম' },
  { value: 'admission', label: 'admission — ভর্তি তথ্য' },
  { value: 'exam', label: 'exam — পরীক্ষার তথ্য' },
  { value: 'gallery', label: 'gallery — গ্যালারি' }
]
const CATEGORY_RANK = new Map(CATEGORY_OPTIONS.map((c, i) => [c.value, i]))
const CATEGORY_LABEL = new Map(CATEGORY_OPTIONS.map(c => [c.value, c.label]))

export default function MenuItemsTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<MenuItem> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<MenuItem>(`SELECT * FROM menu_items WHERE institution_eiin = ? ORDER BY sort_order ASC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('menu_items', (editing as any).id, rec)
      else await d1Insert('menu_items', rec)
      onStatus({ type: 'success', text: 'Menu item saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  // Sort by canonical category order first so items in the same category
  // are clustered together, then by sort_order within that category.
  const groupedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const rankA = CATEGORY_RANK.get(a.category_key) ?? 999
      const rankB = CATEGORY_RANK.get(b.category_key) ?? 999
      if (rankA !== rankB) return rankA - rankB
      return (a.sort_order ?? 0) - (b.sort_order ?? 0)
    })
  }, [items])

  return (
    <GenericTable
      title="Menu Items" icon="📋"
      items={groupedItems} loading={loading} editingItem={editing}
      groupBy={{ key: 'category_key', label: (v) => CATEGORY_LABEL.get(v) ?? v }}
      columns={[
        { key: 'category_key', label: 'Category', width: '110px' },
        { key: 'title_bn', label: 'Title (BN)', width: '160px' },
        { key: 'title_en', label: 'Title (EN)', width: '160px' },
        { key: 'slug', label: 'Slug', width: '130px' },
        { key: 'sort_order', label: 'Order', width: '60px' }
      ]}
      formFields={[
        { key: 'category_key', label: 'Category Key', required: true, type: 'select', options: CATEGORY_OPTIONS },
        { key: 'title_bn', label: 'Title (BN)', required: true },
        { key: 'title_en', label: 'Title (EN)', required: true },
        { key: 'slug', label: 'Slug', required: true },
        { key: 'sort_order', label: 'Sort Order', type: 'number' },
        { key: 'content_bn', label: 'Content (BN)', type: 'textarea' },
        { key: 'content_en', label: 'Content (EN)', type: 'textarea' },
        { key: 'external_url', label: 'External URL' }
      ]}
      onAdd={() => setEditing({ category_key: 'about', title_bn: '', title_en: '', slug: '', sort_order: 1 })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('menu_items', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
